/**
 * Deterministic gradient generator for event cards.
 * Produces a CSS linear-gradient string based on a hash of the event name.
 * The same input always produces the same output (pure function, no randomness).
 *
 * Uses HSL color space with constrained saturation (50–70%) and lightness (40–55%)
 * for visually appealing results that work well as background placeholders.
 */

/**
 * Simple string hash function (djb2 variant).
 * Produces a 32-bit unsigned integer from any string input.
 */
function hashString(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    // hash * 33 + charCode
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  // Convert to unsigned 32-bit integer
  return hash >>> 0;
}

/**
 * Generates a deterministic CSS linear-gradient string from an event name.
 *
 * The gradient uses 2 colors derived from a hash of the input string,
 * with constrained HSL values for pleasant visual results.
 *
 * @param eventName - The event name to generate a gradient for
 * @returns A CSS linear-gradient string, e.g. "linear-gradient(135deg, hsl(210, 60%, 45%), hsl(240, 55%, 50%))"
 */
export function generateGradient(eventName: string): string {
  const hash = hashString(eventName);

  // Derive two hue values from different parts of the hash
  const hue1 = hash % 360;
  const hue2 = (hue1 + 40 + ((hash >>> 8) % 60)) % 360; // Offset by 40–100 degrees for contrast

  // Derive saturation and lightness with constrained ranges for pleasant results
  const sat1 = 50 + ((hash >>> 12) % 21); // 50–70%
  const sat2 = 50 + ((hash >>> 16) % 21); // 50–70%
  const light1 = 40 + ((hash >>> 20) % 16); // 40–55%
  const light2 = 40 + ((hash >>> 24) % 16); // 40–55%

  // Fixed gradient angle for consistency
  const angle = 135;

  return `linear-gradient(${angle}deg, hsl(${hue1}, ${sat1}%, ${light1}%), hsl(${hue2}, ${sat2}%, ${light2}%))`;
}
